import type {
  StaticSupportPlaybook,
  StaticSupportPlaybookLink,
  StaticSupportPlaybookStep,
} from "./supportPlaybooks";

const PLAYBOOK_REVIEWED_AT = "2026-04-21T19:00:00.000Z";

const LINKS = {
  workAndIncome: { label: "Work and Income", url: "https://www.workandincome.govt.nz/" },
  inlandRevenue: { label: "Inland Revenue", url: "https://www.ird.govt.nz/" },
  tenancyServices: { label: "Tenancy Services", url: "https://www.tenancy.govt.nz/" },
  ministryJustice: { label: "Ministry of Justice", url: "https://www.justice.govt.nz/family/" },
  employmentNz: { label: "Employment New Zealand", url: "https://www.employment.govt.nz/" },
  ministryEducation: {
    label: "Ministry of Education",
    url: "https://www.education.govt.nz/parents-and-caregivers/",
  },
  schoolEnrolment: {
    label: "New Zealand Government",
    url: "https://www.govt.nz/browse/education/school-and-college/enroling-your-child-in-school/",
  },
  healthNz: { label: "Health New Zealand", url: "https://info.health.nz/" },
  disabilitySupport: {
    label: "Disability Support Services",
    url: "https://www.disabilitysupport.govt.nz/",
  },
  sorted: { label: "Sorted", url: "https://sorted.org.nz/" },
  moneyTalks: { label: "MoneyTalks", url: "https://www.moneytalks.co.nz/" },
  parentHelp: {
    label: "Parent Help",
    url: "https://www.parenthelp.org.nz/parenting-helpline/",
  },
  plunketLine: {
    label: "PlunketLine",
    url: "https://www.plunket.org.nz/about-plunket/what-we-offer/plunketline/",
  },
  parentToParent: { label: "Parent to Parent", url: "https://parent2parent.org.nz/" },
  adhdNz: { label: "ADHD New Zealand", url: "https://www.adhd.org.nz/" },
  communityLaw: { label: "Community Law", url: "https://communitylaw.org.nz/" },
  cab: { label: "Citizens Advice Bureau", url: "https://www.cab.org.nz/" },
  support1737: { label: "1737", url: "https://1737.org.nz/" },
  govtNz: { label: "New Zealand Government", url: "https://www.govt.nz/" },
  passports: { label: "New Zealand Passports", url: "https://www.passports.govt.nz/" },
  familyServices: {
    label: "Family Services Directory",
    url: "https://www.familyservices.govt.nz/directory/",
  },
  acc: { label: "ACC", url: "https://www.acc.co.nz/" },
  businessNz: { label: "business.govt.nz", url: "https://www.business.govt.nz/" },
  areYouOk: { label: "Are You OK", url: "https://www.areyouok.org.nz/" },
} as const;

type StepInput = Omit<StaticSupportPlaybookStep, "step">;

const withLinks = (
  ...links: Array<StaticSupportPlaybookLink | null | undefined>
) => links.filter((link): link is StaticSupportPlaybookLink => Boolean(link));

const buildPlaybook = (definition: {
  slug: string;
  title: string;
  description: string;
  icon: string;
  life_event_tags: string[];
  steps: StepInput[];
}): StaticSupportPlaybook => ({
  id: `static-nz-${definition.slug}`,
  slug: definition.slug,
  section: "support",
  title: definition.title,
  description: definition.description,
  icon: definition.icon,
  difficulty: "moderate",
  estimated_time: `${definition.steps.length} steps`,
  life_event_tags: definition.life_event_tags,
  steps: definition.steps.map((step, index) => ({
    ...step,
    step: index + 1,
  })),
  is_active: true,
  sort_order: 0,
  last_reviewed_at: PLAYBOOK_REVIEWED_AT,
  review_frequency_months: 6,
  created_at: PLAYBOOK_REVIEWED_AT,
  updated_at: PLAYBOOK_REVIEWED_AT,
});

export const getNewZealandStaticSupportPlaybook = (
  slug: string,
): StaticSupportPlaybook | null => {
  switch (slug) {
    case "moving-house":
      return buildPlaybook({
        slug,
        title: "Moving House as a Single Parent in New Zealand",
        description:
          "The full checklist from deciding to move to getting your family settled safely in the new place.",
        icon: "📦",
        life_event_tags: ["housing", "moving", "single parent"],
        steps: [
          {
            title: "Check your housing position first",
            description:
              "If you rent, look at your tenancy type, notice rules, and bond evidence before you promise a move date. If safety is the issue, start with urgent housing or family violence support first.",
            actions: [
              "Check the tenancy rules before you agree to dates or hand keys back.",
              "Treat safety as the first task if the move is tied to violence or coercion.",
            ],
            links: withLinks(LINKS.tenancyServices, LINKS.workAndIncome, LINKS.areYouOk),
          },
          {
            title: "Get your paperwork together early",
            description:
              "Keep your tenancy agreement, bond details, rent records, inspection photos, school information, and any parenting orders or written care arrangements in one place.",
            actions: ["Build one live folder you can reach quickly when someone asks for proof."],
          },
          {
            title: "Tell the people who affect your money",
            description:
              "Update Work and Income, Inland Revenue, childcare, and anyone involved in child support or care arrangements as soon as the move is real.",
            actions: ["Do not wait until after the move if the change is already certain."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Sort the school or early learning plan before the boxes",
            description:
              "Check school zones, out-of-zone rules, enrolment schemes, transport, and whether your child can stay where they are if that matters most.",
            actions: ["Treat school continuity as part of the move plan, not a separate job for later."],
            links: withLinks(LINKS.schoolEnrolment, LINKS.ministryEducation),
          },
          {
            title: "Photograph the old place properly",
            description:
              "Take clear photos of each room, meter readings, keys returned, and anything you disagree about. Save emails and messages if there may be a bond dispute later.",
            actions: ["Make sure the evidence could make sense to someone who was not there."],
            links: withLinks(LINKS.tenancyServices),
          },
          {
            title: "Re-run the support check for the new situation",
            description:
              "A change of address, rent, board, childcare, transport, or care arrangement can change Accommodation Supplement, Working for Families, or other help.",
            actions: ["Check the support picture again once the new housing costs are clear."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Update health and support contacts",
            description:
              "Move GP, pharmacy, school contacts, and any disability, mental health, or family support services that need the new address or the new local office.",
            actions: ["Tell the services that would matter first in an urgent week."],
            links: withLinks(LINKS.healthNz, LINKS.disabilitySupport),
          },
          {
            title: "Keep the first week simple",
            description:
              "Focus on beds, food, school bags, medicine, and routines before trying to make the whole house feel finished.",
            actions: ["Aim for safe and functional first; settled can come later."],
          },
        ],
      });
    case "going-self-employed":
      return buildPlaybook({
        slug,
        title: "Setting Up Self-Employed from Home in New Zealand",
        description:
          "What to do in the first 90 days so your work is legal, practical and easier to manage when life is already full.",
        icon: "💻",
        life_event_tags: ["work", "self-employment", "tax"],
        steps: [
          {
            title: "Decide what kind of work you are actually doing",
            description:
              "Be clear whether you are a sole trader, contractor, or something more formal, because that changes tax, ACC, and rights.",
            actions: ["Check the business setup before you invoice in the wrong structure."],
            links: withLinks(LINKS.businessNz),
          },
          {
            title: "Use your IRD number properly from day one",
            description:
              "Keep tax separate in your head and in your records from the first payment, even if you keep things simple to begin with.",
            actions: ["Build one habit now instead of cleaning up a whole year later."],
            links: withLinks(LINKS.inlandRevenue),
          },
          {
            title: "Check whether GST will matter",
            description:
              "Many people can start without it, but if your turnover heads toward the threshold you need to know before you invoice the wrong way.",
            actions: ["Check GST early rather than trying to fix it later."],
            links: withLinks(LINKS.inlandRevenue),
          },
          {
            title: "Keep records from the first payment",
            description:
              "Save invoices, receipts, mileage, subscriptions, phone costs, and home-office records in one system you will still use when you are tired.",
            actions: ["Choose one record-keeping system and stick to it."],
            links: withLinks(LINKS.inlandRevenue, LINKS.businessNz),
          },
          {
            title: "Plan for tax before the first year-end",
            description:
              "Put money aside regularly so income tax and provisional tax do not become a crisis later.",
            actions: ["Treat tax as money already spoken for."],
            links: withLinks(LINKS.inlandRevenue),
          },
          {
            title: "Understand ACC and what happens if you cannot work",
            description:
              "Self-employed work can stop instantly when you are sick or caring for a child, so know what ACC does and does not cover.",
            actions: ["Check cover before you need it."],
            links: withLinks(LINKS.acc),
          },
          {
            title: "Check how the work changes your wider support",
            description:
              "Self-employment income can affect Work and Income help, childcare help, and Working for Families, so update your information early.",
            actions: ["Do not assume agencies will read the change from each other."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Choose working patterns that fit your care reality",
            description:
              "Build around school runs, holidays, childcare, and the days when your child needs you unexpectedly.",
            actions: ["Design for the real week, not the ideal week."],
          },
        ],
      });
    case "separation-divorce":
      return buildPlaybook({
        slug,
        title: "Separation and Parenting — the First Steps",
        description:
          "The first things to deal with when family life has changed and you need a calmer order to the next few weeks.",
        icon: "⚖️",
        life_event_tags: ["separation", "co-parenting", "family law"],
        steps: [
          {
            title: "Start with immediate safety and stability",
            description:
              "If anyone is unsafe, use family violence support first. If safety is not the issue, focus first on the children’s day-to-day care, routines, and handovers.",
            actions: ["Separate safety from the rest of the admin if you need to."],
            links: withLinks(LINKS.areYouOk, LINKS.ministryJustice),
          },
          {
            title: "Write down the care pattern as it is now",
            description:
              "Even if it is temporary, note who the children are with, school pickups, weekends, costs, and what is already agreed.",
            actions: ["Keep the record factual and current."],
          },
          {
            title: "Keep parenting and money as linked but separate issues",
            description:
              "You may need to discuss care arrangements, child support, Working for Families, and housing at the same time, but they are not the same system.",
            actions: ["Do not let one disagreement swallow every other task."],
            links: withLinks(LINKS.inlandRevenue, LINKS.workAndIncome),
          },
          {
            title: "Use Parenting Through Separation early if you can",
            description:
              "It helps many parents get clearer about what children need and can make later steps less adversarial.",
            actions: ["Take the structured route before things harden if that is still safe."],
            links: withLinks(LINKS.ministryJustice),
          },
          {
            title: "If agreement is hard, look at Family Dispute Resolution",
            description:
              "Mediation can be a useful next step unless safety or urgency makes the court route more appropriate.",
            actions: ["Use the dispute-resolution route before assuming court is the only next move."],
            links: withLinks(LINKS.ministryJustice),
          },
          {
            title: "Update the agencies that need the truth now",
            description:
              "Relationship status, address, care percentages, and household income can all affect payments and entitlements.",
            actions: ["Update the agencies early so the paperwork does not lag behind reality."],
            links: withLinks(LINKS.inlandRevenue, LINKS.workAndIncome),
          },
          {
            title: "Keep your records calm and factual",
            description:
              "Save messages, dates, agreements, and changes without turning the notes into a running argument.",
            actions: ["Write for evidence, not for emotional release."],
          },
          {
            title: "Get legal help earlier than you think",
            description:
              "Relocation, violence, passport issues, court urgency, or serious disagreement about the children are all signs to get legal help sooner.",
            actions: ["Early legal signposting is often safer than waiting for a crisis."],
            links: withLinks(LINKS.communityLaw, LINKS.cab, LINKS.ministryJustice),
          },
        ],
      });
    case "getting-ehcp":
      return buildPlaybook({
        slug,
        title: "Getting Support at School for Your Child",
        description:
          "A practical route through the first concerns, meetings and referrals when you think your child needs more support.",
        icon: "📚",
        life_event_tags: ["education", "additional needs", "school"],
        steps: [
          {
            title: "Start with what you are seeing",
            description:
              "Write down examples of learning, behaviour, communication, or sensory difficulties you are seeing at home and what school is reporting.",
            actions: ["Bring examples, not just labels, into the first conversation."],
          },
          {
            title: "Ask for a meeting with the class teacher or key contact",
            description:
              "Go in with your examples and ask what support has already been tried and what they are noticing in class.",
            actions: ["Ask what is happening now, not only what might happen later."],
            links: withLinks(LINKS.ministryEducation),
          },
          {
            title: "Ask who coordinates support in the school or kura",
            description:
              "That may be a learning support coordinator, RTLB contact, or another staff lead. Knowing the right person matters.",
            actions: ["Find the person who owns the next step."],
          },
          {
            title: "Follow up in writing",
            description:
              "A short email after meetings creates a timeline of concerns, requests, agreed strategies, and next steps.",
            actions: ["Keep the timeline legible and calm."],
          },
          {
            title: "Ask what can happen now, not only after a diagnosis",
            description:
              "Many children can get practical support, adjustments, or monitoring before formal diagnosis or higher-level funding.",
            actions: ["Push for practical help now, not only formal labels later."],
            links: withLinks(LINKS.ministryEducation),
          },
          {
            title: "If concerns started before school, ask how support will carry across",
            description:
              "Transition planning matters and children often lose momentum when no one owns the handover.",
            actions: ["Ask who is responsible for the transition plan."],
            links: withLinks(LINKS.ministryEducation),
          },
          {
            title: "Ask which assessment or funding route may fit",
            description:
              "That might mean learning support services, ORS-related discussion, disability support, or health referrals.",
            actions: ["Get clear on the next route instead of trying to navigate all of them at once."],
            links: withLinks(LINKS.ministryEducation, LINKS.disabilitySupport),
          },
          {
            title: "Use outside help when needed",
            description:
              "Parent to Parent, ADHD NZ, Community Law, and disability services can help you understand the system and the language schools use.",
            actions: ["Bring outside support in when the school process is not clear enough."],
            links: withLinks(LINKS.parentToParent, LINKS.adhdNz, LINKS.communityLaw),
          },
        ],
      });
    case "going-back-to-work":
      return buildPlaybook({
        slug,
        title: "Going Back to Work After a Gap",
        description:
          "How to work out whether a job actually works for your household before you commit to it.",
        icon: "💼",
        life_event_tags: ["work", "returning to work", "childcare"],
        steps: [
          {
            title: "Start with the reality of your week",
            description:
              "School runs, childcare, transport, sick-child cover, and handovers matter as much as the job itself.",
            actions: ["Plan from the real week, not the best-case week."],
          },
          {
            title: "Cost the job honestly",
            description:
              "Look at travel, holiday care, lunches, tax, and what will change in Working for Families or Work and Income support.",
            actions: ["Check whether the job still works once family costs are included."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Check whether the hours are actually flexible",
            description:
              "A role can sound part-time but still fail in practice if the start time, finish time, or rota cannot move when life does.",
            actions: ["Ask how flexibility works in practice, not only on paper."],
            links: withLinks(LINKS.employmentNz),
          },
          {
            title: "Ask early about flexibility",
            description:
              "New Zealand law gives you a formal route to ask, even if the answer is not automatic.",
            actions: ["Use the formal route rather than hoping it sorts itself out later."],
            links: withLinks(LINKS.employmentNz),
          },
          {
            title: "Do not ignore the contract type",
            description:
              "Casual, fixed-term, and contractor work can all look flexible at first and then become unstable or expensive in different ways.",
            actions: ["Check the contract type before you say yes."],
            links: withLinks(LINKS.employmentNz),
          },
          {
            title: "Get clear on leave before the first emergency happens",
            description:
              "You need to know how sick leave, family violence leave, and unpaid time might work in real life.",
            actions: ["Do not wait for the first crisis to read the leave rules."],
            links: withLinks(LINKS.employmentNz),
          },
          {
            title: "Keep the first version of success modest",
            description:
              "A stable job that fits your care load can be better than a bigger role that breaks the household every second week.",
            actions: ["Choose sustainable first, not just impressive."],
          },
          {
            title: "Re-run your money support once you start",
            description:
              "Work can change childcare help, tax credits, and housing help, so update things promptly.",
            actions: ["Update the agencies once the job is real."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
        ],
      });
    case "christmas-one-income":
      return buildPlaybook({
        slug,
        title: "School Holidays and Special Occasions on One Income",
        description:
          "How to reduce the panic around school breaks, birthdays and seasonal pressure without pretending it is easy.",
        icon: "🎄",
        life_event_tags: ["money", "holidays", "single income"],
        steps: [
          {
            title: "Look at the calendar earlier than feels necessary",
            description:
              "School holidays, birthdays, camp fees, and end-of-year costs are easier to manage when they are not a surprise.",
            actions: ["Put the pressure dates in one place before the term runs away with you."],
          },
          {
            title: "Check which childcare routes are available first",
            description:
              "OSCAR, holiday programmes, relatives, shared care, or lower-cost local activities all need booking or asking before the last minute.",
            actions: ["Start with care and coverage before you think about extras."],
            links: withLinks(LINKS.workAndIncome),
          },
          {
            title: "Plan around the most expensive days",
            description:
              "You do not need a perfect plan for every hour, but you do need a plan for the days when you are working and your child is not at school.",
            actions: ["Identify the hardest days first."],
          },
          {
            title: "Use free and low-cost options deliberately",
            description:
              "Libraries, parks, community centres, museum days, walking tracks, and local events count — this does not need to become a spending competition.",
            actions: ["Build the plan around what actually helps, not what looks impressive."],
            links: withLinks(LINKS.familyServices),
          },
          {
            title: "Talk to your children early about what is and is not possible",
            description:
              "Calm expectation-setting is easier than a stressed no after a whole picture has built up in their head.",
            actions: ["Say the truth early and gently."],
          },
          {
            title: "Ask for help sooner if food, gifts or activity costs are becoming unmanageable",
            description:
              "One-off help, budgeting help, or local family support can be easier to access before everything has tipped over.",
            actions: ["Treat early help as practical, not dramatic."],
            links: withLinks(LINKS.workAndIncome, LINKS.moneyTalks),
          },
          {
            title: "Keep birthdays and holidays separate from your worth as a parent",
            description:
              "Children usually remember safety, connection, and routine more than a perfectly funded season.",
            actions: ["Aim for steady and warm, not perfect."],
            links: withLinks(LINKS.parentHelp),
          },
          {
            title: "Start a tiny future buffer if you can",
            description:
              "Even a small weekly amount for school holidays, birthdays, or December pressure can soften the next round.",
            actions: ["Use a tiny regular habit rather than waiting for spare money to appear."],
            links: withLinks(LINKS.sorted),
          },
        ],
      });
    case "renting-goes-wrong":
      return buildPlaybook({
        slug,
        title: "Your Rights When Renting Goes Wrong",
        description:
          "What to do when repairs are ignored, rent pressure rises or you think the tenancy has moved from difficult to unfair.",
        icon: "🏠",
        life_event_tags: ["housing", "renting", "tenancy"],
        steps: [
          {
            title: "Write down exactly what the problem is",
            description:
              "Repairs, mould, notice issues, access, rent increase, bond, or harassment all need slightly different evidence.",
            actions: ["Name the problem clearly before you try to solve it."],
          },
          {
            title: "Go back to the tenancy agreement and Tenancy Services",
            description:
              "Make sure you know what the actual rule is before the argument turns into competing opinions.",
            actions: ["Use the official tenancy route before you escalate."],
            links: withLinks(LINKS.tenancyServices),
          },
          {
            title: "Put requests in writing",
            description:
              "Dates, photos, emails, and texts are what help later if the problem turns into a formal dispute.",
            actions: ["Write as if someone new may need to read it later."],
          },
          {
            title: "Do not stop paying rent just because repairs are not done",
            description:
              "Use the proper routes instead so you do not create a second problem on top of the first.",
            actions: ["Keep the payment issue and the repair issue separate."],
          },
          {
            title: "Ask for the specific fix or outcome you want",
            description:
              "That might be a repair by a date, a corrected notice, bond repayment, or a clear response on access or rent.",
            actions: ["Be clear about the outcome, not only the frustration."],
          },
          {
            title: "Use Tenancy Services information and mediation early",
            description:
              "It is often the fastest way to work out whether you are right, partly right, or missing a step.",
            actions: ["Use the neutral route before the positions harden."],
            links: withLinks(LINKS.tenancyServices),
          },
          {
            title: "Apply to the Tribunal if you have genuinely tried and it is still not resolved",
            description:
              "Organise your evidence so someone who knows nothing about the situation can follow it.",
            actions: ["Build the evidence for a stranger, not just yourself."],
            links: withLinks(LINKS.tenancyServices),
          },
          {
            title: "Get extra help if safety, family violence, children’s health or homelessness risk is part of it",
            description:
              "Housing disputes do not happen in a vacuum, and sometimes the bigger problem needs help first.",
            actions: ["Use urgent support if the tenancy problem is part of a wider crisis."],
            links: withLinks(LINKS.areYouOk, LINKS.workAndIncome),
          },
        ],
      });
    case "benefits-check":
      return buildPlaybook({
        slug,
        title: "Support Check — Are You Missing Help?",
        description:
          "A calm step-by-step pass through the main New Zealand support systems when money is tight or your circumstances have changed.",
        icon: "💰",
        life_event_tags: ["benefits", "money", "single parent"],
        steps: [
          {
            title: "Start with the household facts as they are now",
            description:
              "Who the children live with, your relationship status, housing costs, income, and childcare situation all matter.",
            actions: ["Use the current facts, not the version from a few months ago."],
          },
          {
            title: "Check Work and Income and Inland Revenue separately",
            description:
              "They run different systems, and people often update one but not the other.",
            actions: ["Do not assume one agency will correct the other."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Look first at the big regular supports",
            description:
              "Sole Parent Support, Working for Families, Best Start, Accommodation Supplement, childcare help, and child support usually matter most.",
            actions: ["Start with the larger ongoing supports before the smaller extras."],
            links: withLinks(LINKS.workAndIncome, LINKS.inlandRevenue),
          },
          {
            title: "Then check the pressure points",
            description:
              "Urgent food or bill help, Temporary Additional Support, disability-related help, and school-age care support can still make a big difference.",
            actions: ["Check the supports that match the current pressure, not only the main payment."],
            links: withLinks(LINKS.workAndIncome, LINKS.moneyTalks),
          },
          {
            title: "Do not forget changes after separation",
            description:
              "Care percentages, address, partner status, and shared care can all alter entitlement.",
            actions: ["Run the check again when separation changes the facts."],
            links: withLinks(LINKS.inlandRevenue, LINKS.workAndIncome),
          },
          {
            title: "Check the disability and caring routes deliberately",
            description:
              "Those supports sit in different places and are easy to miss if a child has extra care needs.",
            actions: ["Treat disability and caring support as its own check, not an afterthought."],
            links: withLinks(LINKS.workAndIncome, LINKS.disabilitySupport),
          },
          {
            title: "If you own your home, remember rates and local-cost help",
            description:
              "A rates rebate or other council-linked help can sit outside the payments people check first.",
            actions: ["Do not assume support ends because you own instead of rent."],
            links: withLinks(LINKS.govtNz),
          },
          {
            title: "Use a person as well as a website if you are confused",
            description:
              "Work and Income, CAB, MoneyTalks, and Community Law can all help you work out the next practical step.",
            actions: ["If the system is still muddy, bring in a real person."],
            links: withLinks(LINKS.cab, LINKS.moneyTalks, LINKS.communityLaw),
          },
        ],
      });
    case "co-parenting-hard":
      return buildPlaybook({
        slug,
        title: "Co-Parenting When It Is Hard",
        description:
          "What to focus on when contact, communication or practical arrangements are difficult and you still need the children’s week to work.",
        icon: "🤝",
        life_event_tags: ["co-parenting", "family law", "communication"],
        steps: [
          {
            title: "Reduce the problem to the next issue, not the whole relationship",
            description:
              "Handover times, school decisions, holidays, money, and communication may each need different handling.",
            actions: ["Pick the next practical problem instead of trying to solve the whole history."],
          },
          {
            title: "Keep communication factual and child-focused where you can",
            description:
              "Short, clear messages are often safer than trying to resolve history and logistics in one thread.",
            actions: ["Write for clarity, not for emotional closure."],
          },
          {
            title: "Write down what is agreed and what is not",
            description:
              "Even small practical agreements are easier to hold onto when they are recorded somewhere both parents can refer to.",
            actions: ["Keep the record simple enough to use."],
          },
          {
            title: "Use Parenting Through Separation or Family Dispute Resolution if direct agreement is stuck",
            description:
              "Structure can help when informal discussion keeps failing and safety is not the barrier.",
            actions: ["Bring in structure early if talking alone is going nowhere."],
            links: withLinks(LINKS.ministryJustice),
          },
          {
            title: "Know when not to mediate",
            description:
              "If there is family violence, coercion, fear, or a serious risk issue, get legal or specialist support instead of forcing a co-parenting model that is not safe.",
            actions: ["Do not call something co-parenting if the situation is unsafe."],
            links: withLinks(LINKS.areYouOk, LINKS.communityLaw),
          },
          {
            title: "Separate the children’s care from the children being used as messengers",
            description:
              "Ask schools and services to communicate directly with both parents where appropriate rather than through the child.",
            actions: ["Take practical pressure off the child wherever you can."],
          },
          {
            title: "Keep evidence if agreements are being broken",
            description:
              "Calm records are more useful than angry summaries when agreements keep shifting or a child is being put in the middle.",
            actions: ["Record facts, dates, and changes."],
          },
          {
            title: "Get legal help sooner if travel, relocation, repeated non-compliance or safety are involved",
            description:
              "Those issues harden quickly and usually need specialist advice earlier than ordinary co-parenting friction does.",
            actions: ["Treat the higher-risk issues as a signal to get help now."],
            links: withLinks(LINKS.communityLaw, LINKS.passports, LINKS.ministryJustice),
          },
        ],
      });
    case "building-support-network":
      return buildPlaybook({
        slug,
        title: "Building Your Support Network",
        description:
          "How to find real people and practical support when family life feels too small, too quiet or too heavy.",
        icon: "💗",
        life_event_tags: ["support", "community", "wellbeing"],
        steps: [
          {
            title: "Start with the type of support you actually need",
            description:
              "Emotional support, childcare backup, school pickups, budgeting help, and legal information are different needs and often come from different places.",
            actions: ["Name the kind of help before you go looking for it."],
          },
          {
            title: "Use the obvious national routes without embarrassment",
            description:
              "CAB, Community Law, Family Services Directory, Parent Help, PlunketLine, and 1737 exist because people need them.",
            actions: ["Treat these as ordinary supports, not last resorts."],
            links: withLinks(
              LINKS.cab,
              LINKS.communityLaw,
              LINKS.familyServices,
              LINKS.parentHelp,
              LINKS.plunketLine,
              LINKS.support1737,
            ),
          },
          {
            title: "Look for one local thread first, not a perfect village",
            description:
              "A school parent you trust, a local whānau group, a parent programme, or a community centre can be enough to start with.",
            actions: ["Aim for one reliable thread, not the whole network at once."],
            links: withLinks(LINKS.familyServices),
          },
          {
            title: "Ask for specific help",
            description:
              "People usually respond better to a clear ask than to a general statement that things are hard.",
            actions: ["Make the ask small and practical."],
          },
          {
            title: "Use support that fits your child’s age and needs",
            description:
              "Parenting support for a baby, a teenager, or a disabled child can look very different.",
            actions: ["Choose the support that matches the actual stage you are in."],
            links: withLinks(LINKS.parentHelp, LINKS.plunketLine, LINKS.parentToParent),
          },
          {
            title: "Let practical support count as real support",
            description:
              "Help with forms, appointments, meals, rides, or advocacy matters just as much as emotional support.",
            actions: ["Count the practical help too."],
            links: withLinks(LINKS.cab, LINKS.communityLaw),
          },
          {
            title: "Do not wait until you are at breaking point",
            description:
              "Networks are easier to build when the first conversation is not already a crisis.",
            actions: ["Make the first connection before everything is urgent if you can."],
          },
          {
            title: "Keep the people who are helpful close",
            description:
              "A support network grows through repeated small trust, not one big rescue.",
            actions: ["Notice the people and places that actually help, and keep using them."],
          },
        ],
      });
    default:
      return null;
  }
};
